import { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { useAuth } from "../../store/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LogOut,
  ChevronDown,
  User as UserIcon,
  Menu,
} from "lucide-react";
import { ErrorBoundary } from "../common/ErrorBoundary";

/**
 * Admin / Owner shell.
 *
 * Header layout:
 *   - Left: mobile menu button + system name
 *   - Right: user info + Logout (desktop) / avatar dropdown (mobile)
 */
export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Controls the sidebar drawer on mobile screens.
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = (user?.fullName || "U")
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("");

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 h-16 border-b border-slate-200 bg-white">
          <div className="flex h-full items-center justify-between gap-4 px-4 md:px-6 lg:px-8">
            <div className="flex min-w-0 items-center gap-2">
              {/* Mobile sidebar button */}
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open navigation menu"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-slate-700 hover:bg-slate-100 lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>

              <div className="truncate text-xs font-medium text-slate-500 md:text-sm">
                Bar Management System
              </div>
            </div>

            {/* Desktop */}
            <div className="hidden items-center gap-3 md:flex">
              <div className="text-right leading-tight">
                <div
                  className="text-sm font-semibold text-slate-900"
                  data-testid="header-user-name"
                >
                  {user?.fullName}
                </div>

                <div
                  className="text-[11px] uppercase tracking-wider text-slate-500"
                  data-testid="header-user-role"
                >
                  {user?.roleName}
                </div>
              </div>

              <div className="grid h-10 w-10 place-items-center rounded-full bg-navy-700 font-semibold text-white">
                {initials}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={doLogout}
                data-testid="logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>

            {/* Mobile */}
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-slate-100"
                    data-testid="header-user-menu"
                  >
                    <div className="grid h-9 w-9 place-items-center rounded-full bg-navy-700 text-sm font-semibold text-white">
                      {initials}
                    </div>

                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="text-sm font-semibold text-slate-900">
                      {user?.fullName}
                    </div>

                    <div className="text-[11px] uppercase tracking-wider text-slate-500">
                      {user?.roleName}
                    </div>
                  </DropdownMenuLabel>

                  <DropdownMenuSeparator />

                  <DropdownMenuItem disabled>
                    <UserIcon className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={doLogout}
                    data-testid="mobile-logout"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden p-4 sm:p-6 lg:p-8">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};